import type { ColumnSizingState } from '@tanstack/react-table';
import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { ColumnWidthMap } from '../helpers';
import { mergeColumnWidths } from '../helpers';
import type { TableColumn } from '../types';

interface UseTableResizeProps<T> {
  columns: TableColumn<T>[];
  loading: boolean;
  savedColumnWidths: ColumnWidthMap;
  setSavedColumnWidths: (widths: ColumnWidthMap) => void;
  tableWrapperRef: RefObject<HTMLDivElement | null>;
  visibleColumns: TableColumn<T>[];
}

interface UseTableResizeReturn {
  columnSizing: ColumnSizingState;
  handleResizeEnd: (key: string, width: number) => void;
  setColumnSizing: React.Dispatch<React.SetStateAction<ColumnSizingState>>;
  widths: ColumnWidthMap;
}

export const useTableResize = <T>(params: UseTableResizeProps<T>): UseTableResizeReturn => {
  const { columns, savedColumnWidths, setSavedColumnWidths, tableWrapperRef, loading, visibleColumns } = params;

  const [widths, setWidths] = useState<ColumnWidthMap>(() => mergeColumnWidths(columns, savedColumnWidths));

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => {
    const sizing: ColumnSizingState = {};
    const initialWidths = mergeColumnWidths(columns, savedColumnWidths);

    Object.entries(initialWidths).forEach(([key, value]) => {
      if (typeof value === 'number') {
        sizing[key] = value;
      }
    });

    return sizing;
  });

  const handleResizeEnd = useCallback(
    (key: string, width: number): void => {
      const newWidths: ColumnWidthMap = {
        ...widths,
        [key]: width
      };

      setWidths(newWidths);
      setSavedColumnWidths(newWidths);
      setColumnSizing((previous) => ({ ...previous, [key]: width }));
    },
    [widths, setSavedColumnWidths]
  );

  // Initialize widths from DOM on first render
  useEffect(() => {
    if (!loading) {
      const heads = tableWrapperRef?.current?.querySelectorAll('th');

      if (heads) {
        let newWidth = {};

        visibleColumns.forEach(({ resizable = true, ...item }, index) => {
          if (resizable) {
            const width = heads[index]?.offsetWidth;

            if (width) {
              newWidth = { ...newWidth, [item.key]: width };
            }
          }
        });

        setSavedColumnWidths(newWidth);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    widths,
    columnSizing,
    setColumnSizing,
    handleResizeEnd
  };
};
